import { CommonModule, DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { NotificationJob, NotificationJobPagedRequest, NotificationType } from '../model';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { NotificationsService } from '../notifications.service';
import { ToolbarModule } from 'primeng/toolbar';
import { NotificationTypePipe } from '../pipes/notification-type.pipe';
import { DatePickerModule } from 'primeng/datepicker';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { FileUpload, FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-notification-jobs-list',
  imports: [
    FormsModule, InputTextModule, FileUploadModule, DialogModule, CommonModule,
    TableModule, ButtonModule, ToolbarModule, DatePickerModule,
    DatePipe, NotificationTypePipe,
    InputNumberModule, SelectModule,
    ConfirmDialog
  ],
  providers: [ConfirmationService],
  templateUrl: './notification-jobs-list.component.html',
  styleUrl: './notification-jobs-list.component.scss'
})
export class NotificationJobsListComponent {

  rangeDates: (Date | undefined)[] = [new Date(), undefined];

  autoRenewalNotificationsVisible = false;
  importDialogVisible = false;
  developerMode = false;

  autoRenewalDays = 30;

  notificationTypes: any = [];
  @ViewChild('fu')
  fu: FileUpload | undefined;

  constructor(private service: NotificationsService, private confirmationService: ConfirmationService) {
    const notificationTypePipe = new NotificationTypePipe();

    this.notificationTypes = [
      { name: notificationTypePipe.transform(NotificationType.RenewalOffer), value: NotificationType.RenewalOffer },
      { name: notificationTypePipe.transform(NotificationType.UpcomingRenewalNotification), value: NotificationType.UpcomingRenewalNotification },
    ]
  }

  public req: NotificationJobPagedRequest = {
    page: 0,
    pageSize: 20,
  };

  protected data: NotificationJob[] = [];
  protected total: number = 0;
  protected isLoading: boolean = false;
  protected editingNotification: NotificationJob | null = null;
  protected notificationModal: boolean = false;
  protected submitted: boolean = false;

  toogleDeveloperMode() {
    this.developerMode = !this.developerMode;
  }
  showImportDialog() {
    this.importDialogVisible = true;
  }

  showAutoRenewalDialog() {
    this.autoRenewalNotificationsVisible = true;
  }

  onUpload(event: FileUploadHandlerEvent) {
    if (event.files.length > 0) {
      const file = event.files[0];
      const formData = new FormData();
      formData.append('file', file, file.name);

      this.service.importNotificationsFromCSV(formData).subscribe(response => {
        console.log('File uploaded successfully', response);
        this.importDialogVisible = false;
        this.loadDataLazy({ first: 0, sortOrder: this.req.orderBy ? 1 : 0, sortField: this.req.sort });
      }, error => {
        console.error('File upload failed', error);
      });
    }
  }

  submitUpload() {
    if (this.fu) {
      this.fu.upload();
    }
  }

  loadDataLazy(ev: TableLazyLoadEvent) {
    this.req.orderBy = ev.sortField as string;
    this.req.orderASC = ev.sortOrder == 1;
    this.req.page = ev.first ?? 0;
    this.req.from = this.rangeDates[0];
    this.req.to = this.rangeDates[1];

    this.service.list({
      pageSize: this.req.pageSize,
      page: this.req.page / this.req.pageSize,
      from: this.req.from,
      to: this.req.to,
      orderBy: this.req.orderBy,
      orderASC: this.req.orderASC,
      filterByNotificationType: this.req.filterByNotificationType
    }).subscribe(data => {
      this.data = data.items;
      this.total = data.total;
    });
  }

  refresh() {
    this.loadDataLazy({ first: 0 });
  }

  editItem(notification: NotificationJob) {
    this.editingNotification = {
      ...notification,
      notificationDate: new Date(notification.notificationDate),
      dateSent: notification.dateSent == null ? undefined : new Date(notification.dateSent),
      deal: undefined
    };
    this.notificationModal = true;
    this.submitted = false;
  }

  editItemSubmit() {
    this.submitted = true;
    if (this.editingNotification == null ||
      this.editingNotification.notificationDate == null ||
      this.editingNotification.type == null ||
      this.editingNotification.to == null || this.editingNotification.to == "") {
      return;
    }

    this.service.updateNotification(this.editingNotification!).subscribe(() => {
      this.loadDataLazy({ first: this.req.page });
      this.notificationModal = false;
      this.submitted = false;
      this.editingNotification = null;
    });
  }

  deleteItem(notification: NotificationJob) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete the selected notification job?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.service.deleteNotification(notification).subscribe(() => {
          this.loadDataLazy({ first: this.req.page });
        });
      }
    });
  }

  createAutoRenewalTasks() {
    this.confirmationService.confirm({
      message: 'Are you sure you want to generate auto renewal jobs. This action cannot be undone?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.service.generateAutoRenewalNotifications(this.autoRenewalDays).subscribe(() => {
          this.autoRenewalNotificationsVisible = false;
          this.loadDataLazy({ first: 0 });
        });
      }
    });
  }

  processPendingNotifications() {
    this.confirmationService.confirm({
      message: 'Possible pending emails will be sent. Are you sure?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.service.processPendingNotifications().subscribe(() => {
          this.loadDataLazy({ first: this.req.page });
        });
      }
    });
  }

  exportData(): void {
    this.service.exportNotifications(this.req).subscribe((response: Blob) => {
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    });
  }
}
